/**
 * pendingRegisterPrefill — short-lived storage for registration form values
 * captured from a deep link before the user reaches RegisterScreen.
 *
 * Mirrors the web's URL-driven prefill behavior in NewRegister (first_name,
 * last_name, email, code are read from the query string). On mobile a deep
 * link can land while the app is bootstrapping, locked, on Login, or even
 * already authenticated, so we persist the recognized fields with a short
 * TTL and let RegisterScreen consume them on mount.
 *
 * Lifecycle:
 *   - Set when RootNavigator detects a /register?... deep link (cold launch
 *     or warm reopen).
 *   - Read once by RegisterScreen on mount, then cleared.
 *   - Auto-discarded after TTL_MS so stale values don't attach to an unrelated
 *     future signup on the same device.
 *
 * Persisted shape: { prefill: RegisterPrefill, savedAt: number } as JSON.
 *
 * To support new URL params: add the field to RegisterPrefill and populate it
 * from `extractRegisterParams` in RootNavigator — no changes needed here.
 */

import AsyncStorage from '@react-native-async-storage/async-storage';
import debug from './debug';

const STORAGE_KEY = '@eleplan/pending_register_prefill';
// 30 days — long enough for install → onboarding → signup, short enough that
// a link shared months ago won't unexpectedly pre-populate a future signup.
const TTL_MS = 30 * 24 * 60 * 60 * 1000;

export interface RegisterPrefill {
  first_name?: string;
  last_name?: string;
  email?: string;
  code?: string;
  /**
   * Partner SUBDOMAIN from the co-branded landing's `/register?ref=<subdomain>`
   * handoff. Distinct from `code` (a pre-minted invite coupon): the backend's
   * /partners/referral/link mints `ref` into a real child coupon at submit.
   * Without this field the native deep-link path silently dropped the partner
   * attribution — the web path only worked because RegisterScreen could still
   * read `window.location.search`.
   */
  ref?: string;
}

interface StoredPrefill {
  prefill: RegisterPrefill;
  savedAt: number;
}

const sanitize = (prefill: RegisterPrefill | null | undefined): RegisterPrefill => {
  const out: RegisterPrefill = {};
  if (!prefill) return out;
  const trim = (v: unknown) => (typeof v === 'string' ? v.trim() : '');
  const firstName = trim(prefill.first_name);
  const lastName = trim(prefill.last_name);
  const email = trim(prefill.email);
  const code = trim(prefill.code);
  const ref = trim(prefill.ref);
  if (firstName) out.first_name = firstName;
  if (lastName) out.last_name = lastName;
  if (email) out.email = email.toLowerCase();
  if (code) out.code = code;
  if (ref) out.ref = ref;
  return out;
};

export const setPendingRegisterPrefill = async (prefill: RegisterPrefill | null | undefined): Promise<void> => {
  const sanitized = sanitize(prefill);
  if (!Object.keys(sanitized).length) return;
  try {
    const payload: StoredPrefill = { prefill: sanitized, savedAt: Date.now() };
    await AsyncStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    debug.log('storage', 'Pending register prefill stored', { keys: Object.keys(sanitized) });
  } catch (err) {
    debug.warn('Failed to store pending register prefill', err);
  }
};

export const getPendingRegisterPrefill = async (): Promise<RegisterPrefill | null> => {
  try {
    const raw = await AsyncStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredPrefill;
    if (!parsed?.prefill) return null;
    if (typeof parsed.savedAt === 'number' && Date.now() - parsed.savedAt > TTL_MS) {
      await AsyncStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return sanitize(parsed.prefill);
  } catch (err) {
    debug.warn('Failed to read pending register prefill', err);
    return null;
  }
};

export const clearPendingRegisterPrefill = async (): Promise<void> => {
  try {
    await AsyncStorage.removeItem(STORAGE_KEY);
  } catch {
    // Non-critical — next read will return whatever is there.
  }
};
